<script setup lang="ts">
import { computed, ConcreteComponent, defineEmits, defineProps, ref, RenderFunction } from 'vue'
import { KycDocumentResponse } from '../../../../utilities/api'

import {
    CheckCircleIcon,
    DotsCircleHorizontalIcon,
    ExclamationCircleIcon,
    StarIcon,
    XCircleIcon,
} from '@heroicons/vue/solid'
import { i18n } from '../../../../localization'
import { formatDate, formatDateTime } from '../../../../utilities/helpers'
import BaseButtonMuted from '../../../base/BaseButtonMuted.vue'
import { BankAccountStatus, KycDocumentVerificationStatus, KycDocumentVerificationType } from '../../../../typings'
import InlineAlert from '../../../base/InlineAlert.vue'

const props = defineProps<{
    kycDocument: KycDocumentResponse
    verificationType: KycDocumentVerificationType
    icon: RenderFunction
}>()

const emit = defineEmits(['updated-bank-account'])

interface KycStatusLayoutProps {
    icon: ConcreteComponent
    text: string
    description: string
    class: string
    textClass: string
}

const verificationStatusOptions: { [key in KycDocumentVerificationStatus]: KycStatusLayoutProps } = {
    'in-progress': {
        icon: DotsCircleHorizontalIcon,
        text: i18n.global.t('verification_kyc_document.in_progress'),
        description: i18n.global.t('verification_kyc_document.in_progress_description'),
        class: 'text-blue-400',
        textClass: 'text-blue-900',
    },
    'on-hold': {
        icon: DotsCircleHorizontalIcon,
        text: i18n.global.t('verification_kyc_document.on_hold'),
        description: i18n.global.t('verification_kyc_document.on_hold_description'),
        class: 'text-orange-400',
        textClass: 'text-orange-900',
    },
    'accepted': {
        icon: CheckCircleIcon,
        text: i18n.global.t('verification_kyc_document.accepted'),
        description: i18n.global.t('verification_kyc_document.accepted_description'),
        class: 'text-green-500',
        textClass: 'text-green-900',
    },
    'accepted-not-eligible-for-investment': {
        icon: ExclamationCircleIcon,
        text: i18n.global.t('verification_kyc_document.accepted'),
        description: i18n.global.t('verification_kyc_document.accepted_not_eligible_for_withdrawal_description'),
        class: 'text-yellow-500',
        textClass: 'text-yellow-900',
    },
    'rejected': {
        icon: XCircleIcon,
        text: i18n.global.t('verification_kyc_document.rejected'),
        description: i18n.global.t('verification_kyc_document.rejected_description'),
        class: 'text-red-400',
        textClass: 'text-red-900',
    },
    'unreadable': {
        icon: ExclamationCircleIcon,
        text: i18n.global.t('verification_kyc_document.unreadable'),
        description: i18n.global.t('verification_kyc_document.unreadable_description'),
        class: 'text-red-400',
        textClass: 'text-red-900',
    },
    'expired': {
        icon: ExclamationCircleIcon,
        text: i18n.global.t('verification_kyc_document.expired'),
        description: i18n.global.t('verification_kyc_document.expired_description'),
        class: 'text-orange-400',
        textClass: 'text-orange-900',
    },
    'wrong-type': {
        icon: ExclamationCircleIcon,
        text: i18n.global.t('verification_kyc_document.wrong_type'),
        description: i18n.global.t('verification_kyc_document.wrong_type_description'),
        class: 'text-red-400',
        textClass: 'text-red-900',
    },
    'wrong-name': {
        icon: ExclamationCircleIcon,
        text: i18n.global.t('verification_kyc_document.wrong_name'),
        description: i18n.global.t('verification_kyc_document.wrong_name_description'),
        class: 'text-red-400',
        textClass: 'text-red-900',
    },
    'duplicate-document': {
        icon: ExclamationCircleIcon,
        text: i18n.global.t('verification_kyc_document.duplicate_document'),
        description: i18n.global.t('verification_kyc_document.duplicate_document_description'),
        class: 'text-red-400',
        textClass: 'text-red-900',
    },
}

const computedVerificationStatus = computed(() => {
    if (
        props.verificationType === KycDocumentVerificationType.KycBank &&
        props.kycDocument.verification_status === KycDocumentVerificationStatus.ACCEPTED &&
        (!props.kycDocument.bankAccount || props.kycDocument.bankAccount.status !== BankAccountStatus.VERIFIED)
    ) {
        return KycDocumentVerificationStatus.ACCEPTED_NOT_ELIGIBLE_FOR_WITHDRAWAL
    }
    return props.kycDocument.verification_status
})

const verificationStatusProps = computed(() => {
    return verificationStatusOptions[computedVerificationStatus.value]
})

const showDetails = ref(false)
</script>

<template>
    <div class="rounded-md bg-gray-50 px-6 py-5 shadow-sm">
        <div class="sm:flex sm:items-start sm:justify-between">
            <div class="flex-grow sm:flex sm:items-start">
                <Component :is="icon" class="inline-block w-8"></Component>
                <div class="mt-3 flex-grow sm:ml-4 sm:mt-0">
                    <div class="flex justify-between text-sm font-medium text-gray-900">
                        <div class="mr-3">
                            {{ $t(`verification_kyc_document.type.${kycDocument.type}`) }}
                        </div>
                        <div class="flex items-center">
                            <div class="pr-2 text-right">
                                <p :class="verificationStatusProps.textClass">{{ verificationStatusProps.text }}</p>
                                <p
                                    v-if="
                                        verificationType === KycDocumentVerificationType.KycBank &&
                                        computedVerificationStatus ===
                                            KycDocumentVerificationStatus.ACCEPTED_NOT_ELIGIBLE_FOR_WITHDRAWAL
                                    "
                                    class="text-gray-400">
                                    {{ verificationStatusProps.description }}
                                </p>
                            </div>
                            <div>
                                <Component
                                    :is="verificationStatusProps.icon"
                                    :class="verificationStatusProps.class"
                                    class="block w-5"></Component>
                            </div>
                        </div>
                    </div>
                    <div class="mt-1 flex justify-between text-sm text-gray-600 sm:flex sm:items-center">
                        <div v-if="kycDocument.valid_to">
                            {{ $t('verification_kyc_document.valid_to') }} {{ formatDate(kycDocument.valid_to) }}
                        </div>
                        <span
                            v-if="kycDocument.valid_to && kycDocument.verification_date"
                            class="hidden sm:mx-2 sm:inline"
                            aria-hidden="true">
                            &middot;
                        </span>
                        <div v-if="kycDocument.verification_date" class="mt-1 text-right sm:mt-0">
                            {{ $t('verification_kyc_document.verified_at') }}
                            {{ formatDate(kycDocument.verification_date) }}
                        </div>
                    </div>
                </div>
            </div>
            <div class="mt-4 sm:ml-6 sm:mt-0 sm:flex-shrink-0">
                <BaseButtonMuted @click="showDetails = !showDetails">
                    {{
                        showDetails
                            ? $t('verification_kyc_document.hide_details_button')
                            : $t('verification_kyc_document.show_details_button')
                    }}
                </BaseButtonMuted>
            </div>
        </div>
        <div v-if="kycDocument.verification_comment || showDetails" class="mt-4 border-t pt-2">
            <div class="space-y-1">
                <template v-if="showDetails">
                    <div v-if="kycDocument.bankAccount" class="flex justify-between">
                        <div>
                            <div v-if="kycDocument.bankAccount.holder">
                                <span class="mr-1 text-gray-600"
                                    >{{ $t('verification_kyc_document.account_holder') }}:
                                </span>
                                <span>{{ kycDocument.bankAccount.holder }}</span>
                            </div>
                            <div v-if="kycDocument.bankAccount.iban">
                                <span class="mr-1 text-gray-600">{{ $t('account.iban') }}: </span>
                                <span>{{ kycDocument.bankAccount.iban }}</span>
                            </div>
                            <div v-if="kycDocument.bankAccount.bic">
                                <span class="mr-1 text-gray-600">{{ $t('account.bic') }}: </span>
                                <span>{{ kycDocument.bankAccount.bic }}</span>
                            </div>
                        </div>
                        <div>
                            <small v-if="kycDocument.bankAccount.is_main" class="flex text-sm text-blue-800">
                                {{ $t('bank_account.main_bank_account') }}
                                <StarIcon class="ml-1 w-4" />
                            </small>
                        </div>
                    </div>
                    <InlineAlert
                        v-if="
                            verificationType === KycDocumentVerificationType.KycBank &&
                            computedVerificationStatus ===
                                KycDocumentVerificationStatus.ACCEPTED_NOT_ELIGIBLE_FOR_WITHDRAWAL
                        "
                        slim
                        type="Warning"
                        :title="$t('verification_kyc_document.accepted_not_eligible_for_withdrawal_description')"
                        :description="
                            !props.kycDocument.bankAccount
                                ? $t(
                                      'verification_kyc_document.accepted_not_eligible_for_withdrawal_description_re_upload'
                                  )
                                : $t(
                                      'verification_kyc_document.accepted_not_eligible_for_withdrawal_description_contact_support'
                                  )
                        " />
                    <div>
                        <span class="mr-1 text-gray-600">{{ $t('verification_kyc_document.uploaded_at') }} </span>
                        <span>{{ formatDateTime(kycDocument.created_at) }}</span>
                    </div>
                </template>
                <div
                    v-if="
                        kycDocument.verification_status !== KycDocumentVerificationStatus.ACCEPTED &&
                        kycDocument.verification_comment
                    ">
                    <span class="mr-1 text-gray-600">{{ $t('verification_kyc_document.comment') }}: </span>
                    <span>{{ kycDocument.verification_comment }}</span>
                </div>
            </div>
            <div v-if="showDetails" class="mt-2">
                <object
                    v-if="kycDocument.mime_type === 'application/pdf'"
                    width="100%"
                    height="400px"
                    type="application/pdf"
                    :data="kycDocument.url"></object>
                <img v-else :src="kycDocument.url" class="max-h-96 rounded-md" />
            </div>
        </div>
    </div>
</template>
